import type { CampaignCardView } from "../../../shared/types/campaign-card.type";
import {
	fmtMoney,
	fmtNum,
	fmtRoiPct,
	signedMetricModifier,
} from "../../../shared/utils/campaign-metrics-format";
import { KpiCard, KpiStrip } from "./KpiCard";

type Props = {
	campaign: CampaignCardView;
};

const NO_VALUE = "—";
const NOT_APPLICABLE = "N/A";

/** Heartbeat campaigns have no performance KPIs; null means the backend sent no value. */
function kpiText(
	value: number | null | undefined,
	format: (n: number) => string,
	notApplicable: boolean,
): string {
	if (notApplicable) return NOT_APPLICABLE;
	return value != null ? format(value) : NO_VALUE;
}

function kpiTone(
	value: number | null | undefined,
	notApplicable: boolean,
): string | undefined {
	return !notApplicable && value != null ? signedMetricModifier(value) : undefined;
}

export const CampaignKpiSection = ({ campaign }: Props) => {
	const m = campaign.metrics;
	const kpisNotApplicable =
		campaign.classification?.kpiStatus === "not_applicable";
	const budget = m.budget ?? 0;
	const spentPct =
		budget > 0 ? Math.round((m.cost / budget) * 100) : 0;

	return (
		<>
			<KpiStrip>
				<KpiCard
					label='Budget'
					value={fmtMoney(budget)}
					sub={budget > 0 ? `Spent ${spentPct}%` : undefined}
					editable={campaign.isEditable}
				/>
				<KpiCard label='Cost' value={fmtMoney(m.cost)} />
				<KpiCard
					label='Profit'
					value={kpiText(m.profit, fmtMoney, kpisNotApplicable)}
					valueClassName={kpiTone(m.profit, kpisNotApplicable)}
				/>
				<KpiCard
					label='ROI'
					value={kpiText(m.roi, fmtRoiPct, kpisNotApplicable)}
					valueClassName={kpiTone(m.roi, kpisNotApplicable)}
				/>
				<KpiCard
					label='Installs'
					value={fmtNum(m.inst)}
					sub={`CPI ${kpiText(m.cpi, fmtMoney, kpisNotApplicable)}`}
				/>
				<KpiCard
					label='Revenue'
					value={fmtMoney(m.dep_sum)}
					sub={`${fmtNum(m.dep)} count`}
				/>
				<KpiCard
					label='tCPA'
					value={m.tcpa != null ? fmtMoney(m.tcpa) : NO_VALUE}
					editable={campaign.isEditable}
				/>
				<KpiCard
					label='CPA'
					value={kpiText(m.cpa, fmtMoney, kpisNotApplicable)}
				/>
			</KpiStrip>

			<div className='campaign-detail__settings-grid'>
				<div className='campaign-detail__settings-panel campaign-detail__settings-panel--geo'>
					<div className='campaign-detail__settings-block'>
						<div className='campaign-detail__settings-header'>
							<div className='campaign-detail__settings-label'>GEO</div>
							{campaign.isEditable && (
								<button
									type='button'
									className='campaign-detail__settings-edit'
									disabled>
									Edit
								</button>
							)}
						</div>
						<div className='campaign-detail__settings-value'>
							{campaign.geo.map((g) => g.country).join(", ") || "—"}
						</div>
					</div>
				</div>
				<div className='campaign-detail__settings-panel'>
					<div>
						<div className='campaign-detail__settings-label'>Ad Groups</div>
						<div className='campaign-detail__settings-value'>
							{campaign.adgroups.length}
						</div>
					</div>
				</div>
			</div>
		</>
	);
};
